import React, { useLayoutEffect, useRef } from 'react';
import { Box, ButtonBase, InputBase, SxProps, Theme, Typography } from '@mui/material';
import { LuMic, LuMicOff } from 'react-icons/lu';
import { elliottTokens as t } from '../tokens';
import TalkingFace from './TalkingFace';
import { VoiceInputMode, VoiceUiState } from './useVoiceConversation';

// The Live Scenario's voice controls, replacing the sticky InputBar while the
// spoken conversation is running. Split by whose action it is:
//   top    — Elliott: what she is doing, and her audio (Replay, Pause)
//   below  — the learner: one answer field that their spoken words fill as
//            they talk and that they can also type in, the choice between
//            speaking and typing, and Send answer on the right
// The layout never changes between states or between speaking and typing,
// only words and colour, so nothing moves under the learner's cursor.
// Elliott always speaks as "I": she is a guide in the conversation, not a
// system describing itself in the third person.

const VoiceAvatar: React.FC<{ state: VoiceUiState; levelRef?: React.MutableRefObject<number> }> = ({
  state,
  levelRef
}) => (
  <Box
    sx={{
      position: 'relative',
      flexShrink: 0,
      width: 44,
      height: 44,
      display: 'grid',
      placeItems: 'center',
      opacity: state === 'paused' || state === 'micOff' || state === 'muted' ? 0.55 : 1,
      transition: 'opacity 0.25s ease'
    }}
  >
    <TalkingFace size={40} speaking={state === 'speaking'} levelRef={levelRef} />
    {state === 'listening' && (
      <Box
        sx={{
          position: 'absolute',
          inset: -3,
          borderRadius: '50%',
          border: `2px solid ${t.success}`,
          pointerEvents: 'none',
          animation: 'elliottListenRing 1.6s ease-in-out infinite',
          '@keyframes elliottListenRing': {
            '0%, 100%': { opacity: 0.35, transform: 'scale(1)' },
            '50%': { opacity: 1, transform: 'scale(1.07)' }
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.8 }
        }}
      />
    )}
    {(state === 'thinking' || state === 'preparing') && (
      <Box
        sx={{
          position: 'absolute',
          inset: -3,
          borderRadius: '50%',
          border: '2px solid transparent',
          borderTopColor: t.brandLight,
          pointerEvents: 'none',
          animation: 'elliottThinkSpin 1s linear infinite',
          '@keyframes elliottThinkSpin': { to: { transform: 'rotate(360deg)' } }
        }}
      />
    )}
  </Box>
);

// Three dots at the end of the field while a phrase the learner has said is
// on its way into it. The space is always reserved, so the field never shifts.
const WritingDots: React.FC<{ visible: boolean }> = ({ visible }) => (
  <Box
    aria-hidden
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      width: 26,
      height: 22,
      flexShrink: 0,
      alignSelf: 'flex-end',
      mb: '6px',
      visibility: visible ? 'visible' : 'hidden',
      '& span': {
        width: 5,
        height: 5,
        borderRadius: '50%',
        bgcolor: t.textSecondary,
        animation: 'elliottWriting 1.2s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.7 }
      },
      '& span:nth-of-type(2)': { animationDelay: '0.15s' },
      '& span:nth-of-type(3)': { animationDelay: '0.3s' },
      '@keyframes elliottWriting': {
        '0%, 100%': { opacity: 0.25 },
        '50%': { opacity: 1 }
      }
    }}
  >
    <span />
    <span />
    <span />
  </Box>
);

type Tone = 'amber' | 'green' | 'muted';
const TONE_COLOUR: Record<Tone, string> = { amber: t.brandLight, green: t.success, muted: t.textSecondary };

const describe = (
  state: VoiceUiState,
  pausedDuring: 'speaking' | 'listening' | null,
  heardSpeech: boolean
): { label: string; caption: string; tone: Tone } => {
  switch (state) {
    case 'preparing':
      return { label: 'One moment', caption: 'I’m about to speak.', tone: 'amber' };
    case 'speaking':
      return { label: 'Speaking', caption: 'Pause me any time.', tone: 'amber' };
    case 'listening':
      return {
        label: 'Listening',
        caption: heardSpeech ? 'I’m listening.' : 'I’m ready when you are.',
        tone: 'green'
      };
    case 'thinking':
      return { label: 'One moment', caption: 'I’m considering your response.', tone: 'amber' };
    case 'paused':
      return pausedDuring === 'speaking'
        ? { label: 'Paused', caption: 'Take your time. Resume when you’re ready.', tone: 'muted' }
        : { label: 'Paused', caption: 'Take your time. Nothing is being recorded.', tone: 'muted' };
    case 'muted':
      return {
        label: 'Mic muted',
        caption: 'Nothing is picked up until you turn the mic back on.',
        tone: 'muted'
      };
    case 'micOff':
      return {
        label: 'Mic off',
        caption: 'I’ve closed the mic so nothing is picked up. Resume when you’re ready.',
        tone: 'muted'
      };
    case 'typing':
      return { label: 'Your turn', caption: 'Take your time. Send it when you’re ready.', tone: 'muted' };
    default:
      return { label: 'Roleplay', caption: '', tone: 'muted' };
  }
};

// Buttons carry words only. Primary is the orange gradient with white text,
// disabled is dark (never a faded orange), and a working button says what it
// is doing instead of spinning.
const BUTTON: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  px: '14px',
  py: '8px',
  borderRadius: '8px',
  fontFamily: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 'normal',
  flexShrink: 0,
  border: '1px solid transparent',
  transition: 'color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
  '&.Mui-focusVisible': { outline: `2px solid ${t.brandLight}`, outlineOffset: '2px' }
};

// Outlined clearly enough to read as a button on the dark surface.
const SECONDARY: SxProps<Theme> = {
  bgcolor: t.surface,
  color: t.textPrimary,
  borderColor: t.borderContrast,
  '&:hover': { borderColor: t.textSecondary }
};

// Pause, and the chosen side of Speak answer / Type answer.
const SELECTED: SxProps<Theme> = {
  color: t.brandLight,
  borderColor: t.brandLight,
  bgcolor: 'rgba(247,148,32,0.10)',
  '&:hover': { bgcolor: 'rgba(247,148,32,0.2)' }
};

// Speak answer and Type answer share one outline with a line between them,
// so they read as a choice of one or the other.
const SEGMENT: SxProps<Theme> = {
  ...BUTTON,
  borderRadius: 0,
  border: 'none',
  color: t.textPrimary,
  '&:hover': { bgcolor: 'rgba(253,253,253,0.06)' }
};

const SEGMENT_CHOSEN: SxProps<Theme> = {
  color: t.brandLight,
  bgcolor: 'rgba(247,148,32,0.12)',
  '&:hover': { bgcolor: 'rgba(247,148,32,0.2)' }
};

const PRIMARY: SxProps<Theme> = {
  px: '18px',
  py: '10px',
  fontSize: 14,
  color: t.textPrimary,
  background: `linear-gradient(90deg, ${t.brandLight} 0%, ${t.brandDark} 100%)`,
  '&:hover': { filter: 'brightness(1.08)' }
};

const DISABLED: SxProps<Theme> = {
  bgcolor: 'rgba(0,0,0,0.2)',
  color: t.borderContrast,
  boxShadow: `inset 0 0 0 1px ${t.borderDim}`,
  cursor: 'not-allowed'
};

const PRIMARY_DISABLED: SxProps<Theme> = { px: '18px', py: '10px', fontSize: 14, ...DISABLED };

interface VoiceBarProps {
  state: VoiceUiState;
  pausedDuring: 'speaking' | 'listening' | null;
  inputMode: VoiceInputMode;
  /** The words of the phrase in progress (browser engine only). */
  partial: string;
  /** The open recording has heard real speech. */
  heardSpeech: boolean;
  /** Something the learner said is on its way into the field. */
  writing: boolean;
  /** Elliott is re-reading her last message: Replay becomes Skip. */
  repeating: boolean;
  /** The answer field: spoken phrases land in it, and it takes typing. */
  value: string;
  onChange: (value: string) => void;
  /** Send what is in the field (once the last phrase has reached it). */
  onSend: () => void;
  /** Mute an open mic, or open a closed one. */
  onToggleMic: () => void;
  onInputMode: (mode: VoiceInputMode) => void;
  onReplay: () => void;
  onSkip: () => void;
  onPause: () => void;
  onResume: () => void;
  /** Live speech amplitude 0..1 — drives the talking avatar. */
  levelRef?: React.MutableRefObject<number>;
}

const VoiceBar: React.FC<VoiceBarProps> = ({
  state,
  pausedDuring,
  inputMode,
  partial,
  heardSpeech,
  writing,
  repeating,
  value,
  onChange,
  onSend,
  onToggleMic,
  onInputMode,
  onReplay,
  onSkip,
  onPause,
  onResume,
  levelRef
}) => {
  const fieldRef = useRef<HTMLTextAreaElement | null>(null);
  // Where the learner's caret was, and the value they last typed: a phrase
  // that lands while they are editing must not throw the caret to the end.
  const selectionRef = useRef<{ start: number; end: number } | null>(null);
  const typedValueRef = useRef(value);

  useLayoutEffect(() => {
    const el = fieldRef.current;
    const before = typedValueRef.current;
    if (!el || value === before) return;
    typedValueRef.current = value;
    const focused = document.activeElement === el;
    const sel = selectionRef.current;
    const kept = before.trimEnd();
    const editingEarlier = focused && sel !== null && sel.end < kept.length && value.startsWith(kept);
    if (editingEarlier && sel) el.setSelectionRange(sel.start, sel.end);
    else el.scrollTop = el.scrollHeight;
  }, [value]);

  // Focus the field when the learner chooses to type.
  const chooseMode = (mode: VoiceInputMode) => {
    onInputMode(mode);
    if (mode === 'type') window.setTimeout(() => fieldRef.current?.focus(), 0);
  };

  const { label, caption, tone } = describe(state, pausedDuring, heardSpeech);
  const accent = state === 'listening' ? t.success : state === 'speaking' ? t.brandLight : t.borderDim;
  const glow = accent === t.borderDim ? 'none' : `0px 0px 16px 0px ${accent}40`;
  const holding = state === 'paused' || state === 'micOff';
  const showTransport = state !== 'thinking' && state !== 'idle';
  const canPause = holding || state === 'speaking' || state === 'preparing' || state === 'listening';
  const thinking = state === 'thinking';
  // Elliott has the floor (talking, about to, or paused mid-sentence).
  const elliottsTurn =
    state === 'speaking' || state === 'preparing' || (state === 'paused' && pausedDuring === 'speaking');

  const fieldHasText = Boolean(value.trim());
  const canSend = !thinking && !elliottsTurn && state !== 'idle' && (fieldHasText || writing);

  const micLive = state === 'listening';
  const micCanOpen =
    state === 'muted' ||
    state === 'micOff' ||
    state === 'typing' ||
    (state === 'paused' && pausedDuring === 'listening');
  const micEnabled = micLive || micCanOpen;
  const micLabel = micLive ? 'Mute' : micCanOpen ? 'Unmute' : 'The mic opens when I’ve finished';

  const placeholder = (() => {
    if (elliottsTurn) return 'You can answer when I’ve finished.';
    if (state === 'listening') return 'Speak whenever you’re ready. Your words appear here.';
    if (state === 'muted' || state === 'micOff') return 'The mic is off. You can type here.';
    if (state === 'paused') return 'Mic paused.';
    if (state === 'typing') return 'Type your answer.';
    return '';
  })();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 1146,
        mx: 'auto',
        borderRadius: `${t.radius}px`,
        bgcolor: t.surface,
        display: 'flex',
        flexDirection: 'column',
        gap: '11px',
        px: '14px',
        py: '12px',
        border: `1.5px solid ${accent}`,
        boxShadow: glow,
        transition: 'border-color 0.25s ease, box-shadow 0.25s ease'
      }}
    >
      {/* Elliott. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '13px', flexWrap: 'wrap' }}>
        <VoiceAvatar state={state} levelRef={levelRef} />
        {/* Also the screen-reader surface for every change of state. */}
        <Box aria-live="polite" sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '1.2px',
              textTransform: 'uppercase',
              color: TONE_COLOUR[tone],
              lineHeight: 'normal',
              transition: 'color 0.25s ease'
            }}
          >
            {label}
          </Typography>
          <Typography sx={{ fontSize: 14, color: t.textSecondary, lineHeight: '20px', minHeight: 20 }}>
            {caption}
          </Typography>
        </Box>

        {showTransport && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            {/* During a replay the same slot skips it, so the learner is never
                stuck listening to something they have already heard. */}
            <ButtonBase
              onClick={repeating ? onSkip : onReplay}
              aria-label={repeating ? 'Skip the replay' : 'Replay my last message'}
              sx={{ ...BUTTON, ...SECONDARY }}
            >
              {repeating ? 'Skip' : 'Replay'}
            </ButtonBase>
            {/* One control whose label changes, at the same weight either way:
                pausing must feel like a pause, not like landing somewhere new.
                With the mic already off there is nothing to pause. */}
            <ButtonBase
              onClick={canPause ? (holding ? onResume : onPause) : undefined}
              aria-disabled={!canPause}
              sx={{
                ...BUTTON,
                ...(canPause ? SELECTED : DISABLED),
                ...(canPause && holding ? { bgcolor: 'rgba(247,148,32,0.24)' } : {})
              }}
            >
              {holding ? 'Resume' : 'Pause'}
            </ButtonBase>
          </Box>
        )}
      </Box>

      <Box sx={{ height: '1px', bgcolor: t.borderDim }} />

      {/* The learner: the answer, as spoken or typed. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          px: '8px',
          py: '8px',
          borderRadius: '8px',
          bgcolor: t.bg,
          border: `1px solid ${fieldHasText ? t.borderContrast : t.borderDim}`,
          transition: 'border-color 0.2s ease',
          '&:focus-within': { borderColor: t.borderContrast }
        }}
      >
        <ButtonBase
          onClick={micEnabled ? onToggleMic : undefined}
          aria-disabled={!micEnabled}
          aria-label={micLabel}
          aria-pressed={micEnabled ? !micLive : undefined}
          title={micLabel}
          sx={{
            ...BUTTON,
            width: 36,
            height: 36,
            px: 0,
            py: 0,
            borderColor: micLive ? t.success : t.borderDim,
            bgcolor: micLive ? 'rgba(34,197,94,0.12)' : 'transparent',
            color: micLive ? t.success : micEnabled ? t.textSecondary : t.borderContrast,
            cursor: micEnabled ? 'pointer' : 'not-allowed',
            '&:hover': micEnabled ? { borderColor: micLive ? t.success : t.textSecondary } : {}
          }}
        >
          {micLive || (!micCanOpen && inputMode === 'speak') ? <LuMic size={17} /> : <LuMicOff size={17} />}
        </ButtonBase>
        <Box sx={{ flex: 1, minWidth: 0, py: '6px' }}>
          <InputBase
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            value={value}
            inputRef={fieldRef}
            onChange={(e) => {
              typedValueRef.current = e.target.value;
              onChange(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend) onSend();
              }
            }}
            inputProps={{
              'aria-label': 'Your answer',
              onSelect: (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
                const el = e.currentTarget;
                selectionRef.current = { start: el.selectionStart, end: el.selectionEnd };
              }
            }}
            disabled={thinking}
            placeholder={placeholder}
            sx={{
              p: 0,
              color: t.textPrimary,
              fontSize: 15,
              lineHeight: '22px',
              '& textarea::placeholder': { color: t.textTertiary, opacity: 1 },
              '& textarea': { backgroundColor: 'transparent' },
              // A global stylesheet paints disabled textareas light; same
              // override the InputBar carries.
              '&.Mui-disabled, & .MuiInputBase-input.Mui-disabled, & textarea:disabled': {
                backgroundColor: 'transparent !important',
                WebkitTextFillColor: t.textTertiary,
                boxShadow: 'none !important'
              }
            }}
          />
          {partial && (
            <Typography aria-hidden sx={{ fontSize: 15, lineHeight: '22px', color: t.textTertiary }}>
              {partial}
            </Typography>
          )}
        </Box>
        <WritingDots visible={writing} />
      </Box>

      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}
      >
        <Box
          role="group"
          aria-label="How to answer"
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            borderRadius: '8px',
            border: `1px solid ${t.borderContrast}`,
            overflow: 'hidden'
          }}
        >
          {(['speak', 'type'] as VoiceInputMode[]).map((mode, i) => {
            const chosen = inputMode === mode;
            return (
              <React.Fragment key={mode}>
                {i > 0 && <Box aria-hidden sx={{ width: '1px', bgcolor: t.borderContrast }} />}
                <ButtonBase
                  onClick={thinking ? undefined : () => chooseMode(mode)}
                  aria-pressed={chosen}
                  aria-disabled={thinking}
                  sx={{
                    ...SEGMENT,
                    ...(chosen ? SEGMENT_CHOSEN : {}),
                    ...(thinking ? { cursor: 'default' } : {})
                  }}
                >
                  {mode === 'speak' ? 'Speak Answer' : 'Type Answer'}
                </ButtonBase>
              </React.Fragment>
            );
          })}
        </Box>
        {/* On the right, where a chat puts its send button. Disables the
            instant it is pressed and stays that way until I reply, so it can
            never look dead while the last phrase is being written down. */}
        <ButtonBase
          onClick={canSend ? onSend : undefined}
          aria-disabled={!canSend}
          sx={{ ...BUTTON, ...(canSend ? PRIMARY : PRIMARY_DISABLED) }}
        >
          {thinking ? 'Sending...' : 'Send'}
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default VoiceBar;
